import React from 'react'
import { agentService } from 'core/services'
import { StaticContext, IStaticDataStore } from 'core/contexts/weekly/StaticContext'
import WeeklyContext from 'core/contexts/weekly/WeeklyContext'
import { IBuildedTour, IFilterOptions } from 'utils/weeklyTypes'
import { buildTours } from 'utils/weekly'

interface IWeeklyProviderProps {
  children: React.ReactNode
  initialFilterOptions: IFilterOptions
  initialStartDate: moment.Moment
  initialCalendarMemos: any[]
  posts: any[]
  tasks: any[]
  tourMemoTemplates: any[]
}

export const Provider: React.FC<IWeeklyProviderProps> = ({
  children,
  initialFilterOptions,
  initialStartDate,
  initialCalendarMemos,
  posts,
  tasks,
  tourMemoTemplates,
}) => {
  const [filterOptions, setFilterOptions] = React.useState<IFilterOptions>(initialFilterOptions)
  const [selectedTour, setSelectedTour] = React.useState<IBuildedTour | null>(null)
  const [startDate, setStartDate] = React.useState<moment.Moment>(initialStartDate)
  const [allTours, setAllTours] = React.useState<IBuildedTour[]>([])
  const [calendarMemos, setCalendarMemos] = React.useState<any[]>(initialCalendarMemos)

  const skillsRef = React.useRef([])
  const tourSkillsRef = React.useRef([])
  const meetupPlacesRef = React.useRef([])
  const connectionMailTemplatesRef = React.useRef([])
  const isLoadingRef = React.useRef(false)

  // load 完了時に一度だけ更新して子を再レンダーさせる
  const [staticDataVersion, setStaticDataVersion] = React.useState(0)

  // 非同期でstaticデータを取得
  const loadStaticDataAsync = React.useCallback(async () => {
    if (isLoadingRef.current) {
      return
    }

    isLoadingRef.current = true
    try {
      const [
        skillsResult,
        tourSkillsResult,
        meetupPlacesResult,
        connectionMailTemplatesResult,
      ] = await Promise.all([
        agentService.getAgentSkills(),
        agentService.getAgentTourSkills(),
        agentService.getMeetupPlaces({}),
        agentService.getConnectionMailTemplates(),
      ])

      skillsRef.current = skillsResult.agent_skills || []
      tourSkillsRef.current = tourSkillsResult.agent_tour_skills || []
      meetupPlacesRef.current = meetupPlacesResult.meetup_places || []
      connectionMailTemplatesRef.current =
        connectionMailTemplatesResult.connection_mail_templates || []
      setStaticDataVersion(v => v + 1)
    } catch (error) {
      console.error('Failed to load static data:', error)
    } finally {
      isLoadingRef.current = false
    }
  }, []) // 依存配列を空にして、関数が再生成されないようにする

  // 初回マウント時に非同期でデータを取得
  React.useEffect(() => {
    loadStaticDataAsync()
  }, [])

  const store = React.useMemo<IStaticDataStore>(
    () => ({
      getSkills: () => skillsRef.current,
      getTourSkills: () => tourSkillsRef.current,
      getMeetupPlaces: () => meetupPlacesRef.current,
      getConnectionMailTemplates: () => connectionMailTemplatesRef.current,
    }),
    [staticDataVersion]
  )

  // ツアー更新関数をcontext内で定義
  const onTourUpdate = React.useCallback(
    (updatedTour: IBuildedTour) => {
      const newTours = buildTours(
        allTours.map(tour => (tour.key === updatedTour.key ? updatedTour : tour)),
        posts,
        tasks,
        tourMemoTemplates
      )
      setAllTours(newTours)
      if (selectedTour && selectedTour.key === updatedTour.key) {
        setSelectedTour(newTours.find(tour => tour.key === updatedTour.key))
      }
    },
    [allTours, selectedTour, posts, tasks, tourMemoTemplates]
  )

  // ツアーの情報を直接変更するものではないが、結果的にツアーの変更が行われる時に使用する
  const refetchTour = async (tour_id: number) => {
    try {
      const { tour: refetchedTour } = await agentService.getWeeklyTour(tour_id)
      onTourUpdate(refetchedTour)
    } catch {
      location.reload()
    }
  }

  const value = React.useMemo(
    () => ({
      filterOptions,
      setFilterOptions,
      selectedTour,
      setSelectedTour,
      startDate,
      setStartDate,
      allTours,
      setAllTours,
      calendarMemos,
      setCalendarMemos,
      onTourUpdate,
      refetchTour,
    }),
    [filterOptions, selectedTour, startDate, allTours, calendarMemos, onTourUpdate, refetchTour]
  )

  return (
    <WeeklyContext.Provider value={value}>
      <StaticContext.Provider value={store}>{children}</StaticContext.Provider>
    </WeeklyContext.Provider>
  )
}

export default Provider
